import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Location } from '@/lib/schemas';
import { useAuth } from './useAuth';

interface UseLocationsOptions {
  page?: number;
  limit?: number;
}

export function useLocations(options: UseLocationsOptions = {}) {
  const { page = 1, limit = 10 } = options;
  const { user } = useAuth();

  return useQuery<Location[], Error>({
    queryKey: ['locations', page, limit],
    queryFn: () => api.getLocations(),
    staleTime: 0,
    gcTime: 0,
  });
}

export function useLocation(id: string) {
  return useQuery<Location | null, Error>({
    queryKey: ['locations', id],
    queryFn: () => api.getLocation(id),
    enabled: !!id,
    staleTime: 0,
    gcTime: 0,
  });
}

export function useCreateLocation() {
  const queryClient = useQueryClient();
  return useMutation<Location, Error, Omit<Location, 'location_id' | 'created_at' | 'updated_at'>>({
    mutationFn: (data) => api.createLocation(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
    },
  });
}

export function useUpdateLocation() {
  const queryClient = useQueryClient();
  return useMutation<Location | null, Error, { id: string; data: Partial<Omit<Location, 'location_id' | 'created_at'>>}>({
    mutationFn: ({ id, data }) => api.updateLocation(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
    },
  });
}

export function useDeleteLocation() {
  const queryClient = useQueryClient();
  return useMutation<boolean, Error, string>({
    mutationFn: (id) => api.deleteLocation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
    },
  });
}
